import React from "react";

import { css, cx, keyframes } from "@emotion/css";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import { useInclusionCenterDrawer } from "@/uiKit/atoms/page/PageDrawerState.ts";

// Extra leading on the page-title line that doesn't fit the theme spacing scale.
const TITLE_LINE_HEIGHT_EXTRA = "0.4rem";

export type InclusionCenterFloatingButtonProps = {
  className?: string;
};

export const InclusionCenterFloatingButton: React.FC<InclusionCenterFloatingButtonProps> = ({ className }) => {
  const styles = useStyles(makeStyles);
  const drawer = useInclusionCenterDrawer();

  if (!drawer.isEnabled || drawer.isOpen) {
    return null;
  }

  return (
    <Button
      data-action="inclusion.panel.open"
      variant="text"
      className={cx(styles.root, className)}
      onClick={() => drawer.open()}
    >
      <Icons.addOutlined className={styles.icon} />
    </Button>
  );
};

const scaleIn = keyframes`
  0% {
    scale: 0;
  }
  100% {
    scale: 1;
  }
`;

const makeStyles = (theme: Theme) => {
  const pageTitleHeight = `calc(${FontSizes.h4} + ${TITLE_LINE_HEIGHT_EXTRA} + 2vh + ${theme.spacing(12)})`; // Title + Text line height + margin top + margin bottom

  return {
    root: css({
      position: "sticky",
      top: pageTitleHeight,
      right: 0,
      padding: theme.spacing(2, 4),
      margin: theme.spacing(4, 0),
      backgroundColor: theme.palette.background.paper,
      height: "fit-content",
      width: "fit-content",
      zIndex: 10,
      minWidth: `calc(${FontSizes.subtitleLarge} + ${theme.spacing(4)} * 2 + ${TITLE_LINE_HEIGHT_EXTRA})`,
      ".MuiButton-label": {
        padding: 0,
        margin: 0,
      },
      animation: `${scaleIn} 0.3s ease-in-out`,
    }),
    icon: css({
      width: 26,
      height: 26,
      padding: theme.spacing(1),
      color: theme.palette.text.primary,
    }),
  };
};
